import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { logsService, Log, AvailableColoc } from "../services/logs.service";
import "../styles/AssignLogPage.css";
import { getErrorMessage } from '../utils/error';

const AssignLogPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [log, setLog] = useState<Log | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedColocId, setSelectedColocId] = useState<string>("");
  const [assigning, setAssigning] = useState<boolean>(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  const [assignSuccess, setAssignSuccess] = useState<boolean>(false);
  const [redirectCountdown, setRedirectCountdown] = useState<number>(3);
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [dismissing, setDismissing] = useState<boolean>(false);

  useEffect(() => {
    const fetchLog = async () => {
      try {
        if (!id) throw new Error("Log ID is required");
        const logData = await logsService.getLogById(id);
        setLog(logData);
        setLoading(false);

        // Fetch PDF preview
        if (logData.fileName) {
          try {
            const url = await logsService.getUnassignedFileAsDataUrl(logData.fileName);
            setDataUrl(url);
          } catch {
            // PDF preview not available
          }
        }
      } catch (err) {
        setError("Echec du chargement des details du journal");
        setLoading(false);
      }
    };
    fetchLog();
  }, [id]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (assignSuccess && redirectCountdown > 0) {
      timer = setTimeout(() => {
        setRedirectCountdown((prev) => prev - 1);
      }, 1000);
    } else if (assignSuccess && redirectCountdown === 0) {
      navigate("/to-assign");
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [assignSuccess, redirectCountdown, navigate]);

  const handleBack = () => navigate("/to-assign");

  const handleDismiss = async () => {
    if (!window.confirm("Voulez-vous vraiment rejeter cette facture ?")) return;

    setDismissing(true);
    try {
      await logsService.dismissLog(id!);
      navigate("/to-assign");
    } catch (err) {
      setAssignError(getErrorMessage(err, "Erreur lors du rejet"));
    } finally {
      setDismissing(false);
    }
  };

  const handleColocSelect = (colocId: string) => {
    setSelectedColocId(colocId);
    setAssignError(null);
  };

  const handleAssign = async () => {
    if (!selectedColocId) {
      setAssignError("Veuillez sélectionner une colocation");
      return;
    }

    setAssigning(true);
    setAssignError(null);
    try {
      await logsService.assignColoc(id!, selectedColocId);
      setAssignSuccess(true);
      setRedirectCountdown(3);
    } catch (err) {
      setAssignError(
        getErrorMessage(err, "Echec de l'assignation. Veuillez reessayer.")
      );
    } finally {
      setAssigning(false);
    }
  };

  if (loading) {
    return <div className="loading">Chargement...</div>;
  }

  if (error || !log) {
    return <div className="error">{error || "Journal introuvable"}</div>;
  }

  const availableColocs: AvailableColoc[] = log.context?.availableColocs || [];

  return (
    <div className="assign-log-page">
      <div className="assign-log-header">
        <button className="back-button" onClick={handleBack}>
          &larr; Retour aux factures a assigner
        </button>
        <h1>Assigner une colocation</h1>
      </div>

      <div className="assign-log-content">
        <div className="assign-log-info">
          <div className="info-section">
            <h2>Details de la facture</h2>
            <div className="log-meta">
              <span className="log-meta-item">
                <strong>Fichier:</strong> {log.fileName}
              </span>
              <span className="log-meta-item">
                <strong>Date:</strong> {new Date(log.createdAt).toLocaleString()}
              </span>
              {log.context?.senderEmail && (
                <span className="log-meta-item">
                  <strong>Expediteur:</strong> {log.context.senderEmail}
                </span>
              )}
            </div>

            {assignSuccess && (
              <div className="assign-success">
                Colocation assignee avec succes ! La facture est en cours de traitement.
                Redirection dans {redirectCountdown} secondes...
              </div>
            )}

            {assignError && (
              <div className="assign-error">{assignError}</div>
            )}

            {log.status === "pending_assignment" && !assignSuccess && (
              <div className="coloc-selection">
                <h3>Choisir la colocation</h3>
                <p className="coloc-selection-hint">
                  Selectionnez la colocation a laquelle cette facture correspond.
                </p>

                <div className="coloc-cards">
                  {availableColocs.map((coloc) => (
                    <div
                      key={coloc.id}
                      className={`coloc-card ${selectedColocId === coloc.id ? "selected" : ""}`}
                      onClick={() => handleColocSelect(coloc.id)}
                    >
                      <div className="coloc-card-name">{coloc.name}</div>
                      <div className="coloc-card-address">
                        {coloc.address}, {coloc.zip_code} {coloc.city}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="assign-actions">
                  <button
                    className={`assign-button ${assigning ? "assigning" : ""}`}
                    onClick={handleAssign}
                    disabled={assigning || !selectedColocId || dismissing}
                  >
                    {assigning ? (
                      <>
                        <span className="spinner"></span>
                        Assignation en cours...
                      </>
                    ) : (
                      "Assigner la facture"
                    )}
                  </button>
                  <button
                    className="dismiss-assign-button"
                    onClick={handleDismiss}
                    disabled={assigning || dismissing}
                  >
                    {dismissing ? "Rejet en cours..." : "Rejeter la facture"}
                  </button>
                </div>
              </div>
            )}

            {log.status === "assigned" && (
              <div className="assign-success">
                Cette facture a deja ete assignee et est en cours de traitement.
              </div>
            )}
          </div>
        </div>

        <div className="file-preview-section">
          <h2>Apercu du fichier</h2>
          {dataUrl ? (
            <iframe
              src={dataUrl}
              className="pdf-iframe"
              title="PDF Preview"
            />
          ) : (
            <div className="pdf-fallback">
              Apercu non disponible
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssignLogPage;
